import * as React from "react";
import { EmptyState } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { createDefaultPersonaSetName } from "../../../lib/persona-opportunity-form.js";
import { PERSONA_OPPORTUNITIES_LAYOUT_ID } from "../../../lib/persona-opportunity-stage-chrome.js";
import type {
  CreatePersonaSetSubmit,
  PersonaOpportunityGroupBy,
  PersonaOpportunitySet,
  PersonaOpportunityStatusFilter,
  PersonaOpportunityTabKey,
  PersonaOpportunityViewMode,
} from "../../../types/persona-opportunities.types.js";
import type {
  PersonaOpportunitySetDetail,
  RegeneratePersonaOpportunitySetMode,
} from "../../../api/persona-opportunity-sets.api.js";
import { PersonaOpportunitySetWorkspace } from "../../workspace/PersonaOpportunitySetWorkspace/PersonaOpportunitySetWorkspace.js";
import { CreatePersonaSetDialog } from "../../sets/CreatePersonaSetDialog/CreatePersonaSetDialog.js";
import { DeletePersonaOpportunitySetDialog } from "../../sets/DeletePersonaOpportunitySetDialog/DeletePersonaOpportunitySetDialog.js";
import { RegeneratePersonaOpportunitySetDialog } from "../../sets/RegeneratePersonaOpportunitySetDialog/RegeneratePersonaOpportunitySetDialog.js";
import { RenameSetDialog } from "@/features/opportunities/components/sets/RenameSetDialog/RenameSetDialog.js";
import { PersonaOpportunityHub, type PersonaOpportunityHubProps } from "../PersonaOpportunityHub/PersonaOpportunityHub.js";
import type { OpportunityHubTabKey } from "../PersonaOpportunityHubChrome/hub-chrome.types.js";

/** Stable identity so the hub's queries do not re-key on every render. */
const EMPTY_SET_IDS: string[] = [];

export interface PersonaOpportunitiesPageProps {
  tenantId: string;
  sets: PersonaOpportunitySet[];
  selectedSet: PersonaOpportunitySet | null;
  detail?: PersonaOpportunitySetDetail;
  isDetailLoading?: boolean;
  canListSets?: boolean;
  canCreateSet?: boolean;
  canUseQueryTuning?: boolean;
  isCreatingSet?: boolean;
  isDeletingSet?: boolean;
  isRenamingSet?: boolean;
  isRegeneratingSet?: boolean;
  openCreateSetOnMount?: boolean;
  onCreateSetDialogOpened?: () => void;
  onSetChange: (set: PersonaOpportunitySet | null) => void;
  onOpenCreateSet?: () => void;
  onGenerateSet?: (values: CreatePersonaSetSubmit) => void;
  onStopSet?: (set: PersonaOpportunitySet) => void;
  onDeleteSet?: (set: PersonaOpportunitySet) => void;
  onRenameSet?: (id: string, name: string) => void;
  onRetrySet?: (set: PersonaOpportunitySet) => void;
  onRegenerateSet?: (input: {
    set: PersonaOpportunitySet;
    mode: RegeneratePersonaOpportunitySetMode;
    name?: string;
  }) => void;
  /** Hub (no set open): tab and shareable set scope. */
  hubTab?: OpportunityHubTabKey;
  onHubTabChange?: (tab: OpportunityHubTabKey) => void;
  selectedSetIds?: string[];
  onSelectedSetIdsChange?: (setIds: string[]) => void;
  /** Set card open: scope and landing tab in one navigation. */
  onOpenScopedSet?: (setId: string, hubTab: OpportunityHubTabKey) => void;
  activeTab?: PersonaOpportunityTabKey;
  onTabChange?: (tab: PersonaOpportunityTabKey) => void;
  view?: PersonaOpportunityViewMode;
  onViewChange?: (view: PersonaOpportunityViewMode) => void;
  groupBy?: PersonaOpportunityGroupBy;
  onGroupByChange?: (groupBy: PersonaOpportunityGroupBy) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
  statusFilter?: PersonaOpportunityStatusFilter;
  onStatusFilterChange?: (value: PersonaOpportunityStatusFilter) => void;
  legacyFacets?: PersonaOpportunityHubProps["legacyFacets"];
  onLegacyFacetsChange?: PersonaOpportunityHubProps["onLegacyFacetsChange"];
  highlightOrderId?: string;
  /** Poll hub caches while persona script jobs (incl. Post-Flight double-down) run. */
  hubScriptsLiveRefetchMs?: number | false;
}

export function PersonaOpportunitiesPage({
  tenantId,
  sets,
  selectedSet,
  detail,
  isDetailLoading,
  canListSets = true,
  canCreateSet = true,
  canUseQueryTuning = false,
  isCreatingSet,
  isDeletingSet,
  isRenamingSet,
  isRegeneratingSet,
  openCreateSetOnMount,
  onCreateSetDialogOpened,
  onSetChange,
  onOpenCreateSet,
  onGenerateSet,
  onStopSet,
  onDeleteSet,
  onRenameSet,
  onRetrySet,
  onRegenerateSet,
  hubTab = "sets",
  onHubTabChange,
  selectedSetIds = EMPTY_SET_IDS,
  onSelectedSetIdsChange,
  onOpenScopedSet,
  activeTab = "opportunities",
  onTabChange,
  view = "table",
  onViewChange,
  groupBy,
  onGroupByChange,
  search = "",
  onSearchChange,
  statusFilter = "all",
  onStatusFilterChange,
  legacyFacets,
  onLegacyFacetsChange,
  highlightOrderId,
  hubScriptsLiveRefetchMs = false,
}: PersonaOpportunitiesPageProps) {
  const { t } = useTranslation();
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [pendingDefaultName, setPendingDefaultName] = React.useState(() => createDefaultPersonaSetName());
  const [deleteTarget, setDeleteTarget] = React.useState<PersonaOpportunitySet | null>(null);
  const [renameTarget, setRenameTarget] = React.useState<PersonaOpportunitySet | null>(null);
  const [regenerateTarget, setRegenerateTarget] = React.useState<PersonaOpportunitySet | null>(null);

  const openCreateDialog = React.useCallback(() => {
    setPendingDefaultName(createDefaultPersonaSetName());
    setCreateDialogOpen(true);
    onOpenCreateSet?.();
  }, [onOpenCreateSet]);

  const openCreateDialogRef = React.useRef(openCreateDialog);
  openCreateDialogRef.current = openCreateDialog;

  const didAutoOpenCreateRef = React.useRef(false);
  React.useEffect(() => {
    if (!openCreateSetOnMount || didAutoOpenCreateRef.current) return;
    if (!canCreateSet || !onGenerateSet || selectedSet) return;
    didAutoOpenCreateRef.current = true;
    openCreateDialogRef.current();
    onCreateSetDialogOpened?.();
  }, [openCreateSetOnMount, canCreateSet, onGenerateSet, selectedSet, onCreateSetDialogOpened]);

  return (
    <div className="flex min-h-0 flex-1 flex-col transition-all duration-300 ease-in-out">
      {selectedSet ? (
        <div
          id={PERSONA_OPPORTUNITIES_LAYOUT_ID}
          className="relative mx-auto flex min-h-0 w-full max-w-[1680px] flex-1 flex-col gap-7 overflow-hidden px-8 pt-7 pb-0"
        >
          <PersonaOpportunitySetWorkspace
            selectedSet={selectedSet}
            sets={sets}
            detail={detail}
            isDetailLoading={isDetailLoading}
            activeTab={activeTab}
            view={view}
            canCreateSet={canCreateSet && !!onGenerateSet}
            onTabChange={onTabChange ?? (() => undefined)}
            onViewChange={onViewChange ?? (() => undefined)}
            groupBy={groupBy}
            onGroupByChange={onGroupByChange}
            onBack={() => onSetChange(null)}
            onSelectSet={onSetChange}
            onCreateSet={canCreateSet && onGenerateSet ? openCreateDialog : undefined}
            onRegenerate={
              canCreateSet &&
              onRegenerateSet &&
              selectedSet.status === "READY" &&
              selectedSet.type === "PERSONA"
                ? () => setRegenerateTarget(selectedSet)
                : undefined
            }
            onRetrySet={
              canCreateSet && selectedSet.type !== "USER_GENERATED" ? onRetrySet : undefined
            }
            search={search}
            onSearchChange={onSearchChange ?? (() => undefined)}
            statusFilter={statusFilter}
            onStatusFilterChange={onStatusFilterChange ?? (() => undefined)}
            highlightOrderId={highlightOrderId}
          />
        </div>
      ) : !canListSets ? (
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto px-8 pt-7 pb-16">
          <EmptyState
            icon="lock"
            title={t(
              "personaOpportunities.noListPermissionTitle",
              "You don't have access to Opportunity Hub",
            )}
            description={t(
              "personaOpportunities.noListPermissionDescription",
              "Ask your admin for permission to use Opportunity Hub.",
            )}
          />
        </div>
      ) : (
        <PersonaOpportunityHub
          key={tenantId}
          tenantId={tenantId}
          sets={sets}
          activeTab={hubTab}
          selectedSetIds={selectedSetIds}
          hubScriptsLiveRefetchMs={hubScriptsLiveRefetchMs}
          canCreateSet={canCreateSet}
          isCreatingSet={isCreatingSet}
          onCreateSet={canCreateSet && onGenerateSet ? openCreateDialog : undefined}
          onStopSet={canCreateSet ? onStopSet : undefined}
          onDeleteSet={canCreateSet && onDeleteSet ? setDeleteTarget : undefined}
          onRetrySet={canCreateSet ? onRetrySet : undefined}
          onRenameSet={canCreateSet && onRenameSet ? setRenameTarget : undefined}
          onRegenerateSet={canCreateSet && onRegenerateSet ? setRegenerateTarget : undefined}
          onTabChange={onHubTabChange ?? (() => undefined)}
          onSelectedSetIdsChange={onSelectedSetIdsChange ?? (() => undefined)}
          onOpenScopedSet={onOpenScopedSet}
          legacyFacets={legacyFacets}
          onLegacyFacetsChange={onLegacyFacetsChange}
        />
      )}

      <CreatePersonaSetDialog
        tenantId={tenantId}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        initialName={pendingDefaultName}
        isSubmitting={isCreatingSet}
        canUseQueryTuning={canUseQueryTuning}
        onSubmit={(values) => {
          setCreateDialogOpen(false);
          onGenerateSet?.(values);
        }}
        onUserGeneratedCreated={(created) => {
          setCreateDialogOpen(false);
          if (onOpenScopedSet) {
            onOpenScopedSet(created.id, "scripts");
            return;
          }
          onSelectedSetIdsChange?.([created.id]);
          onHubTabChange?.("scripts");
        }}
      />

      <DeletePersonaOpportunitySetDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        setName={deleteTarget?.name ?? ""}
        isDeleting={isDeletingSet}
        onConfirm={() => {
          if (!deleteTarget) return;
          onDeleteSet?.(deleteTarget);
          setDeleteTarget(null);
        }}
      />

      {renameTarget && (
        <RenameSetDialog
          open={!!renameTarget}
          onOpenChange={(open) => { if (!open) setRenameTarget(null); }}
          currentName={renameTarget.name}
          existingNames={sets.filter((s) => s.id !== renameTarget.id).map((s) => s.name)}
          onSubmit={(name) => {
            onRenameSet?.(renameTarget.id, name);
            setRenameTarget(null);
          }}
          isSubmitting={isRenamingSet}
        />
      )}

      <RegeneratePersonaOpportunitySetDialog
        open={regenerateTarget != null}
        onOpenChange={(open) => {
          if (!open) setRegenerateTarget(null);
        }}
        sourceName={regenerateTarget?.name ?? ""}
        isSubmitting={isRegeneratingSet}
        onConfirm={({ mode, name }) => {
          if (!regenerateTarget) return;
          onRegenerateSet?.({ set: regenerateTarget, mode, name });
          setRegenerateTarget(null);
        }}
      />
    </div>
  );
}
